import { ReactNode, CSSProperties } from "react";
import {
  type ColumnDef,
  type Column,
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnPinningState,
} from "@tanstack/react-table";
import "./table.scss";
import { useNavigate } from "react-router-dom";
import { Pagination } from "./Pagination";

const getCommonPinningStyles = (column: Column<any>): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  return {
    boxShadow: isLastLeftPinnedColumn
      ? "-4px 0 4px -4px gray inset"
      : isFirstRightPinnedColumn
      ? "4px 0 4px -4px gray inset"
      : undefined,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    backgroundColor: isPinned ? "white" : undefined,
    opacity: isPinned ? 0.95 : 1,
    position: isPinned ? "sticky" : "relative",
    // width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
};

const Table = <T,>({
  columns,
  data,
  rowLink,
  paginationProps,
  columnPinning,
}: {
  columns: ColumnDef<T, ReactNode>[];
  data: T[];
  rowLink?: (id: any) => string;
  paginationProps?: {
    page: number;
    setPage: (page: number) => void;
    maxPage: number;
  };
  columnPinning?: ColumnPinningState;
}) => {
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    initialState: { columnPinning },
  });
  const navigate = useNavigate();

  return (
    <div className="table-wrapper">
      <div className="table-scroll-wrapper">
        <table className="table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => {
              return (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const columnRelativeDepth =
                      header.depth - header.column.depth;

                    if (
                      !header.isPlaceholder &&
                      columnRelativeDepth > 1 &&
                      header.id === header.column.id
                    ) {
                      return null;
                    }

                    let rowSpan = 1;
                    if (header.isPlaceholder) {
                      const leafs = header.getLeafHeaders();
                      rowSpan = leafs[leafs.length - 1].depth - header.depth;
                    }
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        rowSpan={rowSpan}
                        style={{ ...getCommonPinningStyles(header.column) }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                className={rowLink ? "clickable" : ""}
                key={row.id}
                onClick={() => {
                  if (!rowLink) return;
                  navigate(rowLink(row.original).toString());
                }}
                style={{
                  cursor: rowLink ? "pointer" : "default",
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td style={{ ...getCommonPinningStyles(cell.column) }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.length === 0 && <p>해당 데이터가 없습니다.</p>}
      {paginationProps && data.length ? (
        <Pagination
          pageIndex={paginationProps.page}
          setPageIndex={paginationProps.setPage}
          maxPage={paginationProps.maxPage}
        />
      ) : null}
    </div>
  );
};

export default Table;
